import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { connectionsApi } from '@/core/api/connections.api'
import { useConnectionHealth } from '@/core/connection/useConnection'
import { useExecutionHistory } from '@/core/hooks'
import { invalidateConnections, queryKeys } from '@/lib/query.client'
import { useToast } from '@/shared/components/toast'
import {
  CircuitBreakerFields,
  RateLimitFields,
  TagsField,
  WorkspaceField,
  emptySettings,
  toSettingsPayload,
  type ConnectionSettingsValue,
} from './ConnectionSettingsFields'
import {
  Callout,
  CopyButton,
  ErrorNotice,
  Loading,
  Logo,
  MethodTag,
  StatusBadge,
  formatDuration,
  relativeTime,
} from '@/shared/components'

export function ConnectionDetailPage() {
  const { connectionId = '' } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  const detail = useQuery({
    queryKey: queryKeys.connection(connectionId),
    queryFn: () => connectionsApi.detail(connectionId),
  })
  const [checking, setChecking] = useState(false)
  const health = useConnectionHealth(connectionId, checking)
  const history = useExecutionHistory({ connection_id: connectionId, limit: 10 })

  const [editing, setEditing] = useState(false)
  const [label, setLabel] = useState('')

  const rename = useMutation({
    mutationFn: (next: string) => connectionsApi.rename(connectionId, next),
    onSuccess: async () => {
      await Promise.all([invalidateConnections(), detail.refetch()])
      setEditing(false)
      toast.success('Renamed')
    },
    onError: (err) => toast.error(err, 'Could not rename'),
  })

  // The non-credential half of the connection, editable in place. Seeded from
  // the server once the detail lands, so an unsaved edit is never clobbered by
  // a background refetch of the same values.
  const [settings, setSettings] = useState<ConnectionSettingsValue>(emptySettings)
  const [settingsDirty, setSettingsDirty] = useState(false)

  const loaded = detail.data?.connection
  useEffect(() => {
    if (!loaded || settingsDirty) return
    setSettings({
      workspaceId: loaded.workspace_id ?? '',
      tags: loaded.tags ?? [],
      rateLimit: loaded.rate_limit,
      circuitBreaker: loaded.circuit_breaker,
    })
  }, [loaded, settingsDirty])

  const editSettings = (next: Partial<ConnectionSettingsValue>) => {
    setSettingsDirty(true)
    setSettings((s) => ({ ...s, ...next }))
  }

  const saveSettings = useMutation({
    mutationFn: () => connectionsApi.update(connectionId, toSettingsPayload(settings)),
    onSuccess: async () => {
      setSettingsDirty(false)
      await Promise.all([invalidateConnections(), detail.refetch()])
      toast.success('Settings saved')
    },
    onError: (err) => toast.error(err, 'Could not save settings'),
  })

  const revoke = useMutation({
    mutationFn: () => connectionsApi.revoke(connectionId),
    onSuccess: async () => {
      await invalidateConnections()
      toast.success('Disconnected')
      navigate('/connections')
    },
    onError: (err) => toast.error(err, 'Could not disconnect'),
  })

  if (detail.isLoading) return <div className="page"><Loading /></div>
  if (detail.error) {
    return (
      <div className="page">
        <ErrorNotice error={detail.error} onRetry={() => detail.refetch()} />
      </div>
    )
  }

  const { connection, actions, declared_scopes } = detail.data!
  const missingScopes = declared_scopes.filter((s) => !connection.scopes.includes(s))

  return (
    <div className="page">
      <Link to="/connections" className="small">
        ← Connections
      </Link>

      <div className="page-header" style={{ marginTop: 14 }}>
        <div className="page-header-row">
          <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
            <Logo
              src={connection.connector_logo}
              name={connection.connector_name ?? connection.connector_id}
              id={connection.connector_id}
            />
            <div>
              {editing ? (
                <div className="row-tight">
                  <input
                    className="input"
                    autoFocus
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && label.trim()) rename.mutate(label.trim())
                      if (e.key === 'Escape') setEditing(false)
                    }}
                  />
                  <button
                    className="btn btn-sm btn-primary"
                    disabled={!label.trim() || rename.isPending}
                    onClick={() => rename.mutate(label.trim())}
                  >
                    Save
                  </button>
                  <button className="btn btn-sm" onClick={() => setEditing(false)}>
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="row-tight">
                  <h1>{connection.label}</h1>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setLabel(connection.label)
                      setEditing(true)
                    }}
                  >
                    Rename
                  </button>
                </div>
              )}
              <p className="small muted" style={{ margin: '5px 0 0' }}>
                {connection.connector_name} · connected {relativeTime(connection.created_at)}
              </p>
            </div>
          </div>
          <div className="row">
            <StatusBadge status={health.data?.status ?? connection.status} />
            <Link
              className="btn btn-primary"
              to={`/run?connection=${connection.connection_id}`}
            >
              Run an action
            </Link>
          </div>
        </div>
      </div>

      {connection.last_error && (
        <Callout kind="danger" title="Last error">
          <p className="small">{connection.last_error}</p>
        </Callout>
      )}

      {health.data && (
        <Callout kind={health.data.healthy ? 'ok' : 'warn'} title="Health check">
          <p className="small">
            {health.data.message}
            {health.data.identity ? ` — signed in as ${health.data.identity}` : ''}
          </p>
        </Callout>
      )}

      <div className="split" style={{ marginTop: 16 }}>
        <div className="stack">
          <div className="card card-pad-0">
            <div className="kv">
              <div>Connection id</div>
              <div className="row-tight">
                <span className="mono truncate">{connection.connection_id}</span>
                <CopyButton value={connection.connection_id} />
              </div>
              <div>Connector</div>
              <div className="mono">{connection.connector_id}</div>
              <div>Auth type</div>
              <div>
                <span className="badge badge-neutral">{connection.auth_type}</span>
              </div>
              <div>Status</div>
              <div>
                <StatusBadge status={connection.status} />
              </div>
              <div>Token expires</div>
              <div>
                {connection.expires_at
                  ? `${new Date(connection.expires_at).toLocaleString()} (${relativeTime(connection.expires_at)})`
                  : 'does not expire'}
              </div>
              <div>Last checked</div>
              <div>{relativeTime(connection.last_checked_at)}</div>
              {typeof connection.metadata?.identity === 'string' && (
                <>
                  <div>Account</div>
                  <div>{connection.metadata.identity as string}</div>
                </>
              )}
            </div>
          </div>

          <div className="card stack">
            <div className="row" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <h3 style={{ margin: 0 }}>Settings</h3>
                <p className="small muted" style={{ margin: '4px 0 0' }}>
                  How this connection is filed, and how hard it may be driven. Changing these
                  never touches its credentials.
                </p>
              </div>
              {settingsDirty && (
                <button
                  className="btn btn-primary btn-sm"
                  disabled={saveSettings.isPending}
                  onClick={() => saveSettings.mutate()}
                >
                  {saveSettings.isPending ? 'Saving…' : 'Save changes'}
                </button>
              )}
            </div>

            <WorkspaceField
              value={settings.workspaceId}
              onChange={(workspaceId) => editSettings({ workspaceId })}
            />
            <TagsField value={settings.tags} onChange={(tags) => editSettings({ tags })} />

            <div className="divider" />

            <RateLimitFields
              value={settings.rateLimit}
              onChange={(rateLimit) => editSettings({ rateLimit })}
            />
            <CircuitBreakerFields
              value={settings.circuitBreaker}
              onChange={(circuitBreaker) => editSettings({ circuitBreaker })}
            />
          </div>

          <div className="card stack" style={{ gap: 9 }}>
            <h3>Granted permissions</h3>
            {connection.scopes.length === 0 ? (
              <p className="small muted" style={{ margin: 0 }}>
                This provider does not report scopes for this auth type.
              </p>
            ) : (
              <div className="row" style={{ gap: 5 }}>
                {connection.scopes.map((scope) => (
                  <span key={scope} className="badge badge-ok mono">
                    {scope}
                  </span>
                ))}
              </div>
            )}
            {missingScopes.length > 0 && (
              <>
                <p className="small muted" style={{ margin: '6px 0 0' }}>
                  Declared by the connector but not granted:
                </p>
                <div className="row" style={{ gap: 5 }}>
                  {missingScopes.map((scope) => (
                    <span key={scope} className="badge badge-neutral mono">
                      {scope}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="row">
            <button
              className="btn"
              disabled={health.isFetching}
              onClick={() => {
                setChecking(true)
                health.refetch()
              }}
            >
              {health.isFetching ? 'Checking…' : 'Test connection'}
            </button>
            <div className="spacer" />
            <button
              className="btn btn-danger"
              disabled={revoke.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Disconnect "${connection.label}"? Its stored credentials are deleted and cannot be recovered.`,
                  )
                ) {
                  revoke.mutate()
                }
              }}
            >
              Disconnect
            </button>
          </div>
        </div>

        <div className="stack">
          <div className="card stack" style={{ gap: 9 }}>
            <h3>Available actions ({actions.filter((a) => !a.is_lookup).length})</h3>
            <div className="stack" style={{ gap: 4 }}>
              {actions
                .filter((a) => !a.is_lookup)
                .map((action) => (
                  <Link
                    key={action.id}
                    to={`/run?connection=${connection.connection_id}&action=${action.id}`}
                    className="row-tight"
                    style={{ padding: '5px 0', color: 'var(--text)' }}
                  >
                    <MethodTag method={action.method} />
                    <span style={{ fontWeight: 530 }}>{action.name}</span>
                  </Link>
                ))}
            </div>
            <Link
              className="btn btn-sm"
              style={{ alignSelf: 'flex-start' }}
              to={`/run?connection=${connection.connection_id}&tab=custom`}
            >
              Or call any endpoint →
            </Link>
          </div>

          <div className="card stack" style={{ gap: 9 }}>
            <div className="row">
              <h3 style={{ flex: 1 }}>Recent runs</h3>
              <Link className="small" to={`/executions?connection=${connection.connection_id}`}>
                View all
              </Link>
            </div>
            {history.isLoading && <Loading label="" />}
            {history.data?.executions.length === 0 && (
              <p className="small muted" style={{ margin: 0 }}>
                Nothing run through this connection yet.
              </p>
            )}
            <div className="stack" style={{ gap: 3 }}>
              {history.data?.executions.map((row) => (
                <Link
                  key={row.id}
                  to={`/executions/${row.id}`}
                  className="row-tight small"
                  style={{ padding: '4px 0', color: 'var(--text)' }}
                >
                  <span
                    className={`badge ${row.status === 'success' ? 'badge-ok' : 'badge-danger'}`}
                  >
                    {row.status_code ?? row.status}
                  </span>
                  <span className="mono" style={{ flex: 1 }}>
                    {row.action_id}
                  </span>
                  <span className="faint">{formatDuration(row.duration_ms)}</span>
                  <span className="faint">{relativeTime(row.executed_at)}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
